<script setup>
import {Head, Link} from "@inertiajs/vue3";

import PageLayout from "@/Layouts/PageLayout.vue";
import GeneralLayout from "@/Layouts/GeneralLayout.vue";
import PracticeThisCTA from "@/Components/Marketing/PracticeThisCTA.vue";
import {BRAND_NAME, cueCardsFAQ} from "@/constants.js";
import FAQ from "@/Components/FAQ.vue";

const metaData = {
    title: 'IELTS Speaking Test Cue Cards',
    description: 'Get ahead in IELTS by speaking with MockReady\'s exclusive cue cards! Explore diverse topics, model answers, and follow-up questions to enhance your skills.'
}
const props = defineProps({
    cueCards: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: ''
    },
    description: {
        type: String,
        default: ''
    }
})
</script>

<template>
    <Head>
        <title>{{ metaData.title }}</title>
        <meta name="description" :content="metaData.description">
    </Head>
    <GeneralLayout>
        <template #header>
            <!--            <h1 class=" py-6 text-4xl font-extrabold text-gray-900 dark:text-white">-->
            <!--                {{ metaData.title }}</h1>-->
        </template>

        <section class="max-w-6xl mx-auto px-4 py-4">
            <h1 class="text-3xl font-bold text-center mb-6">{{ metaData.title }}</h1>
            <p class="text-lg mb-4">
                Are you preparing for the IELTS speaking test and aiming to excel in cue cards? {{ BRAND_NAME }} specializes in
                helping you master IELTS speaking test cue cards, offering practice tips and access to various cue card
                topics to help you accomplish the desired outcome.
            </p>

            <h2 class="text-2xl font-semibold mb-4">Figuring Out the Code</h2>
            <p class="mb-4">
                IELTS cue cards play a pivotal role in the speaking test, demanding a continuous speech delivery on a
                designated topic for 1-2 minutes. Passing this section exhibits your language capability and capacity to
                think and react quickly.
            </p>

            <h2 class="text-2xl font-semibold mb-4">Why Choose {{ BRAND_NAME }} for IELTS Cue Cards Practice?</h2>
            <ul class="list-disc pl-8 mb-4">
                <li>Diverse Cue Card Topics: We offer many cue card topics spanning various subjects, ensuring you can
                    practice diverse topics.
                </li>
                <li>Structured Practice: {{ BRAND_NAME }} provides a systematic approach to cue card practice. Each cue card
                    topic contains guidelines on approaching it, helping you organize your thoughts effectively.
                </li>
                <li>Sample Answers: We provide sample answers for cue card topics, enhancing your understanding and
                    providing insights into responding coherently and comprehensively.
                </li>
                <li>Timed Practice: Duplicating the speaking test, we allow you to practice within time limits, setting
                    you up for the actual test.
                </li>
                <li>Instant Feedback: Get instant feedback for your responses regarding pronunciation, fluency, and
                    coherence, identifying areas for improvement.
                </li>
            </ul>

            <h2 class="text-2xl font-semibold mb-4">How to Make the Most of {{ BRAND_NAME }}'s Cue Card Practice?</h2>
            <ol class="list-decimal pl-8 mb-4">
                <li>Choose a Cue Card Topic: Peruse our extensive collection and select a topic that interests you or
                    aligns with your practice objectives.
                </li>
                <li>Prepare Your Response: Set your clock for 1-2 minutes and start speaking on the chosen topic. Try to
                    cover everything mentioned in the prompt while maintaining fluency and coherence.
                </li>
                <li>Self-Assess: After your response, assess yourself based on the guidelines and sample answers and
                    identify areas needing improvement.
                </li>
                <li>Repeat and Improve: Practice multiple topics to enhance your speaking skills. Focus on refining your
                    responses and addressing any feedback received.
                </li>
                <li>Join the {{ BRAND_NAME }} Community: Engage with fellow IELTS aspirants on our platform. Share your
                    experiences, seek advice, and collaborate to enhance your speaking skills collectively.
                </li>
            </ol>

            <div class="mt-8">
                <h2 class="text-xl font-bold mb-4">Unlock Cue Card Excellence - Try MockReady Today</h2>
                <p class="mb-8">
                    Are you ready to master IELTS speaking test cue cards with confidence and fluency? Try MockReady
                    today and join thousands of IELTS candidates who have improved their cue card performance and
                    succeeded in the speaking test through our platform. Sign up with MockReady and start practicing
                    today. Let us be your dedicated partner in achieving proficiency and confidence in your IELTS
                    speaking exam. The world is waiting to hear your fluent and confident responses – don't keep them
                    waiting!
                </p>

                <PracticeThisCTA page-type="cue_cards_list" placement="bottom"/>
            </div>
        </section>

        <section class="">
            <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 class="text-2xl font-semibold text-center mb-4">IELTS Speaking Cue Cards</h2>
                <section>
                    <article v-for="cueCard in cueCards"
                             class="mb-8 p-6 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                        <div class="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-2">
                            Title: {{ cueCard.id }} - {{ cueCard.title }}
                        </div>
                        <div>
                            <div class="font-semibold">Cue Points</div>
                            <ul class="list-item pl-5">
                                <li v-for="subQuestion in cueCard.sub_questions">
                                    - {{ subQuestion }}
                                </li>
                            </ul>
                        </div>
                        <div class="text-gray-600 dark:text-gray-400">
                            <div class="font-semibold">Answer</div>
                            <ul class="list-disc pl-5">
                                <li v-for="line in cueCard.answer">
                                    {{ line }}
                                </li>
                            </ul>
                        </div>
                        <div class="mt-4">
                            <div class="font-semibold text-gray-800 dark:text-gray-200">Follow Up Questions</div>
                            <ul class="list-disc pl-5 text-gray-600 dark:text-gray-400">
                                <li v-for="followUpQuestion in cueCard.questions">
                                    <div><strong>{{ followUpQuestion.question }}</strong></div>
                                    <div>
                                        <strong>Answer</strong>: {{ followUpQuestion.answer }}

                                    </div>
                                </li>
                            </ul>
                        </div>
                    </article>
                </section>
            </div>
        </section>
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <FAQ :faqItems="cueCardsFAQ"/>
        </div>

    </GeneralLayout>
</template>

<style scoped>

</style>
